import { type FC, useState, useEffect, useRef } from "react";

import { FileText, Plus, Trash2 } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { IconButton } from "@/shared/components/ui/icon-button";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useJsonListAnswer } from "@/shared/hooks/useJsonListAnswer";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { type AdminChildRow, createEmptyChild } from "./adminFamilyTypes";

const HAS_CHILDREN_KEY = "admin_family_has_children";
const CHILDREN_LIST_KEY = "admin_family_children_list";

interface ChildCardProps {
  child: AdminChildRow;
  index: number;
  prefix: string;
  canRemove: boolean;
  nested?: boolean;
  decedentName: string;
  onChange: (index: number, updated: AdminChildRow) => void;
  onRemove: (index: number) => void;
  errors?: Record<string, string>;
}

const ChildCard: FC<ChildCardProps> = ({
  child,
  index,
  prefix,
  canRemove,
  nested,
  decedentName,
  onChange,
  onRemove,
  errors,
}) => {
  const updateField = (field: keyof AdminChildRow, value: string) => {
    const updated = { ...child, [field]: value };
    if (field === "status" && value !== "passed_away") {
      updated.hasChildren = "";
      updated.children = [];
    }
    onChange(index, updated);
  };

  const updateGrandchild = (gcIndex: number, updated: AdminChildRow) => {
    const next = [...child.children];
    next[gcIndex] = updated;
    onChange(index, { ...child, children: next });
  };

  const removeGrandchild = (gcIndex: number) => {
    onChange(index, { ...child, children: child.children.filter((_, i) => i !== gcIndex) });
  };

  const addGrandchild = () => {
    onChange(index, { ...child, children: [...child.children, createEmptyChild()] });
  };

  const handleHasChildrenChange = (value: string) => {
    const updated = { ...child, hasChildren: value };
    if (value === "yes" && child.children.length === 0) {
      updated.children = [createEmptyChild()];
    } else if (value !== "yes") {
      updated.children = [];
    }
    onChange(index, updated);
  };

  const errPrefix = prefix;

  return (
    <div className={cn("relative flex flex-col gap-4 rounded-lg border border-primary p-6", nested ? "bg-subtle" : "bg-white")}>
      {canRemove && (
        <div className="absolute right-2 top-2">
          <IconButton label="Remove" variant="neutral" onClick={() => onRemove(index)}>
            <Trash2 />
          </IconButton>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>First name</Label>
          <Input
            placeholder="First name"
            value={child.firstName}
            onChange={(e) => updateField("firstName", e.target.value)}
            className={errors?.[`${errPrefix}_${index}_first_name`] ? "border-destructive" : ""}
          />
          <FieldError message={errors?.[`${errPrefix}_${index}_first_name`]} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input
            placeholder="Last name"
            value={child.lastName}
            onChange={(e) => updateField("lastName", e.target.value)}
            className={errors?.[`${errPrefix}_${index}_last_name`] ? "border-destructive" : ""}
          />
          <FieldError message={errors?.[`${errPrefix}_${index}_last_name`]} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Are they under 18 years old?</Label>
          <Select value={child.isMinor} onValueChange={(val) => updateField("isMinor", val)}>
            <SelectTrigger className={errors?.[`${errPrefix}_${index}_is_minor`] ? "border-destructive" : ""}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          <FieldError message={errors?.[`${errPrefix}_${index}_is_minor`]} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Status</Label>
          <Select value={child.status} onValueChange={(val) => updateField("status", val)}>
            <SelectTrigger className={errors?.[`${errPrefix}_${index}_status`] ? "border-destructive" : ""}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="living">Living</SelectItem>
              <SelectItem value="passed_away">Passed away</SelectItem>
            </SelectContent>
          </Select>
          <FieldError message={errors?.[`${errPrefix}_${index}_status`]} />
        </div>
      </div>

      {child.status === "passed_away" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Did {child.firstName || "this child"} have children?</Label>
            <Select value={child.hasChildren} onValueChange={handleHasChildrenChange}>
              <SelectTrigger className={errors?.[`${errPrefix}_${index}_has_children`] ? "border-destructive" : ""}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            <FieldError message={errors?.[`${errPrefix}_${index}_has_children`]} />
            <p className="text-xs text-tertiary">
              If {child.firstName || "they"} did, their children take that place on the court's list.
            </p>
          </div>

          {child.hasChildren === "yes" && (
            <div className="flex flex-col gap-4">
              <p className="font-sans text-base font-semibold">
                Add {child.firstName || "their"}'s children
              </p>

              {child.children.map((gc, gcIdx) => (
                <ChildCard
                  key={gc.id}
                  child={gc}
                  index={gcIdx}
                  prefix={`${errPrefix}_${index}_gc`}
                  canRemove={child.children.length > 1}
                  nested
                  decedentName={decedentName}
                  onChange={updateGrandchild}
                  onRemove={removeGrandchild}
                  errors={errors}
                />
              ))}

              <button
                type="button"
                onClick={addGrandchild}
                className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
              >
                <Plus className="size-3.5" />
                Add another child
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AdminChildrenScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";

  const { contacts } = useUserContacts();
  const [seededFromDoc, setSeededFromDoc] = useState(false);
  const seededRef = useRef(false);

  const hasChildren = (answers[HAS_CHILDREN_KEY] as string) ?? "";

  const [children, syncChildren] = useJsonListAnswer<AdminChildRow>(
    answers,
    CHILDREN_LIST_KEY,
    setAnswer as (key: string, value: string) => void,
  );

  useEffect(() => {
    if (seededRef.current || children.length > 0 || hasChildren) return;
    const docChildren = contacts.filter(
      (c) => c.extracted_from_document && c.relationship?.toLowerCase() === "child",
    );
    const allChildren = docChildren.length > 0
      ? docChildren
      : contacts.filter((c) => c.relationship?.toLowerCase() === "child");
    if (allChildren.length === 0) return;
    seededRef.current = true;

    const seeded: AdminChildRow[] = allChildren.map((c) => ({
      ...createEmptyChild(),
      firstName: c.first_name ?? "",
      lastName: c.last_name ?? "",
      status: c.person_status === "passed_away" ? "passed_away" : c.person_status === "living" ? "living" : "",
      isMinor: c.additional_details?.includes("minor") ? "yes" : "",
    }));

    setAnswer(HAS_CHILDREN_KEY, "yes");
    syncChildren(seeded);
    setSeededFromDoc(docChildren.length > 0);
  }, [contacts, hasChildren, children.length, setAnswer, syncChildren]);

  const handleSelect = (value: string) => {
    setAnswer(HAS_CHILDREN_KEY, value);
    if (value === "yes" && children.length === 0) {
      syncChildren([createEmptyChild()]);
    } else if (value !== "yes") {
      syncChildren([]);
      setSeededFromDoc(false);
      seededRef.current = false;
    }
  };

  const updateChild = (index: number, updated: AdminChildRow) => {
    syncChildren((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };

  const removeChild = (index: number) => {
    syncChildren((prev) => prev.filter((_, i) => i !== index));
  };

  const addChild = () => {
    syncChildren((prev) => [...prev, createEmptyChild()]);
  };

  return (
    <div className="flex flex-col gap-8">
      {seededFromDoc && (
        <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
          <FileText className="mt-0.5 size-5 shrink-0 text-brand" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">
              We filled this in from {name}'s will
            </p>
            <p className="text-sm text-secondary">
              Review each answer and confirm it's right, or make a change.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Did {name} have any children?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            Include all biological, adopted, and half children from any relationship. New York
            law treats all legal children equally, and the court requires them listed.
          </p>
        </div>

        <RadioGroup
          name="has_children"
          value={hasChildren}
          onValueChange={handleSelect}
          className="flex flex-col gap-3"
        >
          <OptionCard
            label="Yes"
            selected={hasChildren === "yes"}
            onClick={() => handleSelect("yes")}
            variant="radio"
            value="yes"
          />
          <OptionCard
            label="No children"
            selected={hasChildren === "no_children"}
            onClick={() => handleSelect("no_children")}
            variant="radio"
            value="no_children"
          />
        </RadioGroup>
        <FieldError message={errors?.has_children} />
      </div>

      {hasChildren === "yes" && (
        <div className="flex flex-col gap-4">
          <div className="border-t border-primary" />

          <p className="font-sans text-xl font-semibold leading-[30px]">
            Tell us about {name}'s children
          </p>

          {children.map((child, idx) => (
            <ChildCard
              key={child.id}
              child={child}
              index={idx}
              prefix="child"
              canRemove={children.length > 1}
              decedentName={name}
              onChange={updateChild}
              onRemove={removeChild}
              errors={errors}
            />
          ))}

          <button
            type="button"
            onClick={addChild}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
          >
            <Plus className="size-4" />
            Add another
          </button>
        </div>
      )}
    </div>
  );
};
